/**
 * One place that turns a backend error into something specific a user can read.
 *
 * The API returns a uniform envelope `{ code, message, status, fields? }` (see
 * booqly-api ApiExceptionHandler). `code` is the stable machine reason; this module
 * maps it to friendly copy. Resolution order, so a reason is NEVER lost:
 *   1. curated message for the code (below)
 *   2. a human `message` the server sent (if it isn't just the raw code)
 *   3. a humanized version of the code itself (e.g. invalid_currency → "Invalid currency")
 *
 * The extractor also tolerates older/other shapes (`error` / `detail` / `message`) so
 * the UI stays specific during a backend/frontend deploy skew.
 */

export class ApiError extends Error {
  code: string;
  status?: number;
  fields?: Record<string, string>;
  constructor(code: string, message: string, status?: number, fields?: Record<string, string>) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.fields = fields;
  }
}

/** Curated, user-facing copy for the codes worth a hand-written sentence. Anything
 *  not listed still renders specifically via the humanized-code fallback. */
const MESSAGES: Record<string, string> = {
  // auth / session
  invalid_credentials: "Email or password is incorrect.",
  account_not_active: "Your account isn't active. Contact an admin at your workspace.",
  unauthenticated: "You're not signed in. Please sign in again.",
  access_denied: "You don't have access to this.",
  invalid_session: "Your session is no longer valid. Please sign in again.",
  invalid_refresh: "Your session expired. Please sign in again.",
  email_not_verified: "Verify your email to make changes.",
  mfa_not_enabled: "Two-factor authentication isn't enabled on this account.",
  mfa_not_configured: "Two-factor authentication isn't set up yet.",
  invalid_code: "That code is incorrect. Try again.",
  wrong_token: "That link is invalid. Request a new one.",
  token_expired: "That link has expired. Request a new one.",
  expired: "That link has expired. Request a new one.",
  already_used: "That link has already been used.",
  invalid_or_expired: "That link is invalid or has expired.",
  wrong_purpose: "That link can't be used here.",
  // account / password / email
  password_too_short: "Password must be at least 12 characters.",
  weak_password: "That password is too weak. Use a longer, stronger one.",
  same_password: "Pick a password different from your current one.",
  same_email: "That's already your current email.",
  invalid_email: "Enter a valid email address.",
  email_taken: "Someone else already uses that email in this workspace.",
  email_mismatch: "That email doesn't match.",
  // workspaces / signup
  email_already_owns_workspace:
    "This email already owns a workspace. Sign in to it, or use a different email to start a new one.",
  workspace_name_required: "Enter a name for your workspace.",
  invalid_workspace_name: "Enter a valid workspace name.",
  slug_taken: "That workspace name is already taken. Try another.",
  no_membership: "You're not a member of that workspace.",
  membership_inactive: "Your access to that workspace isn't active.",
  captcha_failed: "Verification failed. Complete the challenge and try again.",
  // bookings / services / customers
  service_not_found: "That service was removed. Pick another.",
  customer_not_found: "Customer not found. Try entering an email instead.",
  customer_required: "Add a client email or pick an existing customer.",
  field_not_in_service: "One of the custom fields no longer belongs to that service.",
  tier_required: "Pick a plan tier to continue.",
  tier_not_offered: "That tier isn't available on this service.",
  addon_not_offered: "One of the add-ons isn't available on this service.",
  booking_not_found: "That booking no longer exists.",
  // inventory / payments
  item_not_found: "That item no longer exists.",
  refund_exceeds_payment: "The refund can't be more than the payment.",
  amount_must_be_positive: "Enter an amount greater than zero.",
  can_only_refund_verified: "Only verified payments can be refunded.",
  payment_method_not_found: "That payment method no longer exists.",
  // uploads
  file_too_large: "That file is too large.",
  not_an_image: "That file isn't an image.",
  unsupported_file_type: "That file type isn't supported.",
  file_required: "Choose a file to upload.",
  // generic buckets
  validation_failed: "Some fields need attention.",
  invalid_body: "The request was malformed or missing required fields.",
  internal_error: "Something went wrong on our end. Please try again.",
};

function titleize(code: string): string {
  const s = code.split(":")[0].replace(/_/g, " ").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Something went wrong. Please try again.";
}

/** code (+ optional server message) → a specific, user-facing sentence. */
export function humanizeApiError(code: string | undefined, serverMessage?: string): string {
  if (!code) return serverMessage || "Something went wrong. Please try again.";

  // Prefixed codes carry a payload after ":" (field label, item name, …).
  if (code.startsWith("missing_required_field:")) {
    return `Missing required field: ${code.slice("missing_required_field:".length)}`;
  }
  if (code.startsWith("addon_out_of_stock:")) {
    return `Out of stock: ${code.slice("addon_out_of_stock:".length)}`;
  }

  const base = code.split(":")[0];
  if (MESSAGES[base]) return MESSAGES[base];

  // A human sentence from the server (not just the raw snake_case code) beats titleizing.
  const looksLikeCode = /^[a-z0-9_]+(:.*)?$/.test(serverMessage ?? "");
  if (serverMessage && serverMessage !== code && !looksLikeCode) return serverMessage;

  return titleize(base);
}

type RawErrorBody = {
  code?: unknown;
  error?: unknown;
  detail?: unknown;
  message?: unknown;
  status?: unknown;
  fields?: unknown;
};

/** Pull {code, message, status, fields} out of whatever the transport surfaced. */
export function extractApiError(raw: unknown): {
  code: string;
  message: string;
  status?: number;
  fields?: Record<string, string>;
} {
  if (typeof raw === "string") {
    return { code: raw, message: humanizeApiError(raw) };
  }
  const o = (raw ?? {}) as RawErrorBody;
  // Prefer fields that hold the machine code; `error` (Spring's phrase) is the last resort.
  const rawCode = o.code ?? o.detail ?? o.message ?? o.error ?? "error";
  const code = String(rawCode);
  const serverMessage = typeof o.message === "string" ? o.message : undefined;
  const status = typeof o.status === "number" ? o.status : undefined;
  const fields =
    o.fields && typeof o.fields === "object" ? (o.fields as Record<string, string>) : undefined;
  return { code, message: humanizeApiError(code, serverMessage), status, fields };
}

/** Build an ApiError (its `.message` is already the friendly sentence) from a raw error/body. */
export function toApiError(raw: unknown): ApiError {
  const { code, message, status, fields } = extractApiError(raw);
  return new ApiError(code, message, status, fields);
}

/**
 * For hand-rolled `fetch` calls: throw a humanized ApiError (reading the response body)
 * on a non-2xx response, instead of a bare `HTTP <status>`. Use after `authFetch`.
 */
export async function throwIfNotOk(res: Response): Promise<void> {
  if (res.ok) return;
  const body = await res.json().catch(() => ({ status: res.status }));
  throw toApiError(body);
}
